import { Children, FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import { Badge, formatDate, HeadingSmall, theme, BodySmall, SIZE, COLOR } from '@arnold/common';
import { Link } from 'react-router-dom';
import { AllowedTopicGroupsQuery, UserQuery } from '../../../generated/hooks';
import { CategoryIcon } from './CategoryIcons';
import { getTopicLanguages } from './utils';
import { getTopicGroupTranslationByLanguageId } from '../../../lib/common';
import { LanguagesIcons } from './LanguagesIcons';
import { InfoBox } from './InfoBox';
import { DateInfo } from './DateInfo';
import { uniqBy } from 'ramda';
import { TopicGroupAction } from './TopicGroupAction';

type TopicGroupType = NonNullable<AllowedTopicGroupsQuery['allowedTopicGroups']>[0];

type ConversationSubjectCardProps = {
  displayCategoryIcon: boolean;
  user: UserQuery['user'];
  topicGroup: TopicGroupType;
  stepTopics: TopicGroupType[];
  organizationLanguages: { code: string }[];
  languageId: number;
  showOptions?: boolean;
  setLoading?: (loading: boolean) => void;
  cannotScheduleReason?: string;
  actionButtonText?: string;
  actionButtonOnClick?: (topicGroup: TopicGroupType) => void;
  requireValidForAction?: boolean;
  freeProductVersion: boolean;
};

const InfoBoxesGrid = styled.div<{ columns: number }>`
  display: grid;
  margin-top: ${theme.spacing.d};
  align-items: center;
  grid-template-columns: ${({ columns }) => {
    if (columns <= 1) return '1fr';
    // First column + middle columns + last wider column for category
    return `2fr ${'2fr '.repeat(columns - 2)}4fr`.trim();
  }};
`;

const InfoBoxes: FC<{ children: ReactNode }> = ({ children }) => {
  const validChildren = Children.toArray(children).filter(Boolean);
  return <InfoBoxesGrid columns={validChildren.length}>{validChildren}</InfoBoxesGrid>;
};

const Description = styled.div`
  // font-size: ${theme.typography.body.small.regular.fontSize};
  margin-top: ${theme.spacing.d};
  color: ${theme.colors.text.secondary};
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DescriptionToggle = styled.div`
  display: inline;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  margin-left: ${theme.spacing.b};
  font-size: ${theme.typography.body.medium.bold.fontSize};
  font-weight: ${theme.typography.body.medium.bold.fontWeight};
  line-height: ${theme.typography.body.medium.bold.lineHeight};
  color: ${theme.colors.text.secondary};
`;

const Card = styled.div`
  background-color: ${theme.background.primary};
  padding: ${theme.spacing.g};
  margin-bottom: ${theme.spacing.e};
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  position: relative;
  box-shadow: ${theme.shadows.elevationContentBlockHigh};
`;

const StepsHeading = styled(BodySmall)`
  color: ${theme.colors.text.secondary};
  font-weight: 500;
  margin-top: ${theme.spacing.e};
  margin-bottom: ${theme.spacing.d};
`;

const StepsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.d};
  align-items: flex-start;
`;

const StepBoxLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.c} ${theme.spacing.f};
  background-color: ${theme.colors.backgroundBasic.default};
  border-radius: 8px;
  width: 200px;
  height: 56px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  border: 1px solid transparent;

  &:hover {
    border: 1px solid ${theme.colors.actionPrimary.default};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${theme.colors.text.secondary};
  color: white;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
  margin-top: 2px;
  line-height: 12px;
`;

const StepName = styled(BodySmall)`
  font-weight: 500;
  color: ${theme.colors.text.primary};
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: flex;
  flex: 1;
  line-height: 1.5;
`;

// 1 character width in pixels - approximately
const oneCharWidth = 7.7;
const getMaxCharByElementWidth = (width: number, maxRows: number) => {
  return Math.floor(width / oneCharWidth) * maxRows;
};

export const TopicGroup: FC<ConversationSubjectCardProps> = ({
  topicGroup,
  topicGroup: {
    name,
    description,
    containsSteps,
    categoryCode,
    lastValidTopic,
    languages,
    lastSurvey,
    lastSurveyGroup,
    id,
    organization,
    steps,
  },
  user,
  stepTopics,
  organizationLanguages,
  languageId,
  displayCategoryIcon,
  showOptions,
  setLoading,
  cannotScheduleReason,
  actionButtonText,
  actionButtonOnClick,
  requireValidForAction,
  freeProductVersion,
}) => {
  const { t } = useTranslation('topicOverview');
  const { t: topicsT } = useTranslation('topics');
  const topic = lastValidTopic;
  const numberOfComments = topic?.numberOfComments;
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const [maxDescriptionChars, setMaxDescriptionChars] = useState<number | null>(null);

  useEffect(() => {
    const update = () => {
      const width = descriptionRef.current?.clientWidth ?? 0;
      if (width <= 0) return;
      setMaxDescriptionChars(getMaxCharByElementWidth(width, 2));
    };

    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
    };
  }, [description]);

  useEffect(() => {
    if (!description) {
      setIsDescriptionExpanded(false);
      return;
    }
    if (maxDescriptionChars !== null && description.length <= maxDescriptionChars) {
      setIsDescriptionExpanded(false);
    }
  }, [description, maxDescriptionChars]);
  const topicLanguages = uniqBy(
    (language) => language.code,
    (topic?.languages || stepTopics.flatMap((stepTopic) => stepTopic.languages ?? []) || languages) as {
      id: string;
      code: string;
    }[],
  );

  const lastUpdatedStepTG = stepTopics.reduce((acc: TopicGroupType | null, stepTopic) => {
    if (
      stepTopic.lastValidTopic?.updatedAt &&
      (new Date(stepTopic.lastValidTopic.updatedAt) > new Date(acc?.lastValidTopic?.updatedAt || '') || !acc)
    ) {
      return stepTopic;
    }
    return acc;
  }, null);

  const lastUpdatedStepTopic = topic || lastUpdatedStepTG?.lastValidTopic;

  // Process steps: get names and sort by startOffsetMins
  type ProcessedStep = { name: string; startOffsetMins: number; id: string };
  const processedSteps: ProcessedStep[] =
    steps && containsSteps
      ? (steps
          .map((step) => {
            const stepTopicGroup = step.stepTopicGroup;
            if (!stepTopicGroup) return null;
            const translation = getTopicGroupTranslationByLanguageId(
              languageId.toString(),
              stepTopicGroup.translations,
            );
            const stepName = translation?.value || stepTopicGroup.name || '';
            return {
              name: stepName,
              startOffsetMins: step.startOffsetMins || 0,
              id: stepTopicGroup.id,
            };
          })
          .filter((step) => step !== null)
          .sort(
            (a, b) => (a as ProcessedStep).startOffsetMins - (b as ProcessedStep).startOffsetMins,
          ) as ProcessedStep[])
      : [];

  const isCustomTopic = organization?.id === user.organization?.id;
  const descriptionText = description || t('noDescription');
  const isDescriptionTruncatable =
    !!description && maxDescriptionChars !== null && description.length > maxDescriptionChars;
  const collapsedChars = Math.max(0, (maxDescriptionChars ?? 0) - 10);
  const collapsedText = isDescriptionTruncatable
    ? `${description.slice(0, collapsedChars).trimEnd()}…`
    : descriptionText;
  const displayedDescriptionText = isDescriptionExpanded || !isDescriptionTruncatable ? descriptionText : collapsedText;

  return (
    <Card data-topic-group-id={id} data-cy="topic-item">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          {displayCategoryIcon && (
            <CategoryIcon
              categoryCode={categoryCode}
              custom={false}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          <HeadingSmall
            className="d-flex align-items-center"
            data-icom={'conversation-subject-card-title'}
            data-cy="topic-item-title"
          >
            {name}
          </HeadingSmall>
          {isCustomTopic && (
            <Badge
              data-cy="custom-topic-badge"
              className="ml-4"
              text={topicsT('customTopic')}
              size={SIZE.SMALL}
              color={COLOR.INFORMATIVE}
              subtle={false}
            />
          )}
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-start">
        <div className="flex-grow-1">
          <Description ref={descriptionRef} data-icom={'conversation-subject-card-description'}>
            {displayedDescriptionText}
            {isDescriptionTruncatable && (
              <DescriptionToggle
                color={theme.colors.text.secondary}
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? t('showLess') : t('showMore')}
              </DescriptionToggle>
            )}
          </Description>

          <InfoBoxes>
            <InfoBox title={''} dataIcom={'conversation-subject-card-languages'}>
              <LanguagesIcons
                languages={getTopicLanguages(topicLanguages as { id: string; code: string }[], organizationLanguages)}
              />
            </InfoBox>
            {lastUpdatedStepTopic && (
              <InfoBox
                dataCy="last-updated-step-topic-info-box"
                showSeparator
                title={t('edited')}
                dataIcom={'conversation-subject-card-updated-by'}
              >
                {lastUpdatedStepTopic.updatedAt
                  ? formatDate(lastUpdatedStepTopic.updatedAt)
                  : formatDate(lastUpdatedStepTopic.createdAt)}
              </InfoBox>
            )}
            {!containsSteps && lastSurvey && (
              <InfoBox
                dataCy="last-survey-info-box"
                showSeparator
                title={t(new Date(lastSurvey?.sendAt) > new Date() ? 'prepared' : 'startAt')}
                dataIcom={'conversation-subject-card-last-survey'}
              >
                {lastSurvey ? <DateInfo sendAt={lastSurvey.sendAt} endsAt={lastSurvey.endsAt} /> : '-'}
              </InfoBox>
            )}
            {containsSteps && lastSurveyGroup && (
              <InfoBox
                dataCy="start-at-info-box"
                showSeparator
                title={t('lastRunSg')}
                dataIcom={'conversation-subject-card-last-survey-group'}
              >
                {lastSurveyGroup?.from ? <DateInfo sendAt={lastSurveyGroup.from} /> : '-'}
              </InfoBox>
            )}
            {!containsSteps && !!numberOfComments && (
              <InfoBox
                showSeparator
                title={t('numberOfCommentsTitle')}
                dataIcom={'conversation-subject-card-number-of-comments'}
              >
                {numberOfComments}
              </InfoBox>
            )}
          </InfoBoxes>
        </div>
        <TopicGroupAction
          topic={topic}
          freeProductVersion={freeProductVersion}
          isCustomTopic={isCustomTopic}
          showOptions={showOptions}
          setLoading={setLoading}
          cannotScheduleReason={cannotScheduleReason}
          actionButtonText={actionButtonText}
          actionButtonOnClick={actionButtonOnClick}
          requireValidForAction={requireValidForAction}
          topicGroup={topicGroup}
          user={user}
        />
      </div>
      {processedSteps.length > 0 && (
        <>
          <StepsHeading>{t('orderedSteps')}</StepsHeading>
          <StepsContainer>
            {processedSteps.map((step, index) => (
              <StepBoxLink
                data-cy="topicGroupStep-title"
                key={index}
                to={`/${isCustomTopic ? 'topicEditor' : 'topicEditorPreview'}/${step.id}`}
              >
                <StepNumber>{index + 1}</StepNumber>
                <StepName>{step.name}</StepName>
              </StepBoxLink>
            ))}
          </StepsContainer>
        </>
      )}
    </Card>
  );
};
